"use client";

import { TrustIcon, FaQIcon } from "@/components/icons";
import { useGetStatisticsQuery } from "@/api/billing/billingApi";
import { formatDateForAPI } from "@/app/billing/utils/dateUtils";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useAppSelector } from "@/store";
import { getBrandConfig, getBrandKeyFromEnv } from "@/lib/branding";
import type { SvgIconProps } from "./icons/types";
import { usePathname } from "next/navigation";

interface NavItem {
  title: string;
  icon: React.ComponentType<SvgIconProps>;
  tooltip: boolean;
  is_coming_soon: boolean;
  href?: string;
  onClick?: () => void;
  isExternal?: boolean;
  hidden?: boolean;
  isActive?: boolean;
}

export function NavExtras() {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const { user } = useAppSelector((s) => s.auth);
  const shouldCheckBilling = !!user && user.group !== "student";
  const today = new Date();
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const billingDateFrom = formatDateForAPI(currentMonthStart);
  const billingDateTo = formatDateForAPI(currentMonthEnd);
  const billingStatistics = useGetStatisticsQuery(
    {
      dateFrom: billingDateFrom,
      dateTo: billingDateTo,
    },
    {
      skip: !shouldCheckBilling,
    },
  );
  const isBillingAvailable =
    billingStatistics.isSuccess &&
    !!billingStatistics.data &&
    billingStatistics.data.status === "success";

  const getDocumentationUrl = () => {
    const isStudent = user?.group === "student";
    const baseUrl = getBrandConfig().supportBaseUrl ?? "https://support.trustexam.ai";

    const activeLocale = locale === "kk" ? "ru" : locale;

    if (isStudent) {
      if (activeLocale === "en") {
        return `${baseUrl}/en/collections/11680666-test-takers`;
      }
      return `${baseUrl}/ru/collections/11680666-\u0441\u0442\u0443\u0434\u0435\u043D\u0442\u044B-\u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0435\u043C\u044B\u0435`;
    }

    return `${baseUrl}/${activeLocale}/`;
  };

  const isExamru = getBrandKeyFromEnv() === "examru";

  const items: NavItem[] = [
    {
      title: t("label-documentation"),
      icon: FaQIcon,
      href: getDocumentationUrl(),
      tooltip: true,
      is_coming_soon: false,
      isExternal: true,
      hidden: isExamru,
    },
    {
      title: t("label-billing"),
      icon: TrustIcon,
      href: "/billing",
      tooltip: true,
      is_coming_soon: !isBillingAvailable,
      hidden: user?.group === "student",
      isActive: pathname.startsWith("/billing"),
    },
  ];

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.filter((item) => !item.hidden).map((item) => (
            <SidebarMenuItem
              key={item.title}
              className="w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center relative"
            >
              <SidebarMenuButton
                asChild={!item.is_coming_soon && !item.onClick}
                tooltip={item.tooltip ? item.title : undefined}
                disabled={item.is_coming_soon}
                isActive={item.isActive}
                className={item.is_coming_soon ? "opacity-50 cursor-not-allowed" : ""}
                onClick={item.onClick}
              >
                {item.is_coming_soon ? (
                  <div className="flex items-center gap-2">
                    <item.icon size={16} />
                    <span className="relative group-data-[collapsible=icon]:hidden">
                      {item.title}
                      <span className="absolute top-1/2 -right-2 translate-x-full -translate-y-1/2 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-md whitespace-nowrap font-medium">
                        {t("coming_soon_label")}
                      </span>
                    </span>
                  </div>
                ) : item.onClick ? (
                  <div className="flex items-center gap-2">
                    <item.icon size={16} />
                    <span>{item.title}</span>
                  </div>
                ) : item.isExternal ? (
                  <a href={item.href!} target="_blank" rel="noopener noreferrer">
                    <item.icon size={16} />
                    <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                  </a>
                ) : (
                  <Link href={item.href!}>
                    <item.icon size={16} />
                    <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                  </Link>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
